import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { manualModules } from '@/data/manualsData';
import { BookOpen, Search, ChevronRight, FileText, ArrowLeft, Shield, AlertTriangle, Lightbulb } from 'lucide-react';

export default function ManualsPage() {
    const { t } = useTranslation();
    const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');

    const selectedModule = manualModules.find(m => m.id === selectedModuleId);

    // Filter modules based on search
    const filteredModules = manualModules.filter(m =>
        t(m.titleKey).toLowerCase().includes(searchQuery.toLowerCase()) ||
        t(m.descriptionKey).toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-black text-white tracking-tight uppercase">
                        <span className="bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">{t('manuals.title')}</span>
                    </h1>
                    <p className="text-gray-400 mt-2 font-light">{t('manuals.subtitle')}</p>
                </div>

                {/* Search Bar */}
                <div className="relative group w-full md:w-96">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search className="h-5 w-5 text-gray-500 group-focus-within:text-blue-400 transition-colors" />
                    </div>
                    <input
                        type="text"
                        className="block w-full pl-10 pr-3 py-3 border border-white/10 rounded-xl leading-5 bg-white/5 text-gray-300 placeholder-gray-600 focus:outline-none focus:bg-white/10 focus:ring-1 focus:ring-blue-500/50 focus:border-blue-500/50 sm:text-sm transition-all"
                        placeholder={t('manuals.searchPlaceholder')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
            </div>

            {/* Content Area */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

                {/* Left Sidebar: Modules List (Visible if no module selected on mobile, or always on desktop if we wanted split view, but let's do master-detail style) */}

                {(!selectedModule || window.innerWidth >= 1024) && (
                    <div className={`lg:col-span-4 space-y-4 ${selectedModule ? 'hidden lg:block' : 'block'}`}>
                        {filteredModules.map((module) => {
                            const Icon = module.icon;
                            const isSelected = selectedModuleId === module.id;
                            return (
                                <button
                                    key={module.id}
                                    onClick={() => setSelectedModuleId(module.id)}
                                    className={`w-full text-left p-5 rounded-2xl border transition-all duration-300 group relative overflow-hidden ${isSelected
                                        ? 'bg-blue-600/10 border-blue-500/50 shadow-[0_0_20px_rgba(59,130,246,0.15)]'
                                        : 'bg-gray-900/40 border-white/5 hover:bg-white/5 hover:border-white/10'
                                        }`}
                                >
                                    <div className="flex items-start gap-4 z-10 relative">
                                        <div className={`p-3 rounded-xl ${isSelected ? 'bg-blue-500 text-white' : 'bg-white/5 text-gray-400 group-hover:bg-white/10 group-hover:text-white'} transition-colors`}>
                                            <Icon className="w-6 h-6" />
                                        </div>
                                        <div className="flex-1">
                                            <h3 className={`text-lg font-bold uppercase tracking-wide mb-1 ${isSelected ? 'text-white' : 'text-gray-300 group-hover:text-white'}`}>
                                                {t(module.titleKey)}
                                            </h3>
                                            <p className="text-sm text-gray-500 leading-relaxed line-clamp-2">
                                                {t(module.descriptionKey)}
                                            </p>
                                        </div>
                                        <ChevronRight className={`w-5 h-5 mt-1 transition-transform ${isSelected ? 'text-blue-400 translate-x-1' : 'text-gray-600 group-hover:text-gray-400'}`} />
                                    </div>

                                    {/* Decoration */}
                                    {isSelected && <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 blur-[50px] rounded-full -mr-10 -mt-10" />}
                                </button>
                            );
                        })}

                        {filteredModules.length === 0 && (
                            <div className="text-center p-10 border border-white/5 rounded-2xl bg-white/[0.02]">
                                <BookOpen className="w-10 h-10 text-gray-600 mx-auto mb-4 opacity-50" />
                                <p className="text-gray-500">{t('manuals.noResults')}</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Right Content: Detail View */}
                <div className={`lg:col-span-8 ${!selectedModule ? 'hidden lg:flex lg:items-center lg:justify-center' : 'block'}`}>
                    {selectedModule ? (
                        <div className="bg-gray-900/40 border border-white/10 rounded-3xl p-8 min-h-[600px] relative overflow-hidden animate-in slide-in-from-right-4 duration-300">
                            {/* Back button for mobile */}
                            <button
                                onClick={() => setSelectedModuleId(null)}
                                className="lg:hidden mb-6 flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                <span className="text-sm font-bold uppercase">{t('manuals.back')}</span>
                            </button>

                            <div className="flex items-center gap-4 mb-8 pb-8 border-b border-white/5">
                                <div className="p-4 bg-blue-500/20 rounded-2xl text-blue-400">
                                    <selectedModule.icon className="w-10 h-10" />
                                </div>
                                <div>
                                    <h2 className="text-3xl font-black text-white uppercase tracking-tight">{t(selectedModule.titleKey)}</h2>
                                    <p className="text-lg text-gray-400 mt-1 mb-3">{t(selectedModule.descriptionKey)}</p>

                                    {/* Roles Badges */}
                                    {selectedModule.targetRoles && (
                                        <div className="flex gap-2 mt-4">
                                            {selectedModule.targetRoles.map((role: string) => (
                                                <span key={role} className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold tracking-wider bg-white/5 border border-white/10 text-gray-300 uppercase">
                                                    <Shield className="w-3 h-3" />
                                                    {role}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="space-y-10">
                                {selectedModule.sections.map((section: any, idx: number) => (
                                    <div key={idx} className="relative pl-6 border-l-2 border-white/10 hover:border-blue-500/50 transition-colors group">
                                        <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-[#050608] border-2 border-white/20 group-hover:border-blue-500 transition-colors" />

                                        <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-3">
                                            {t(section.titleKey)}
                                        </h3>
                                        <p className="text-gray-400 mb-6 leading-relaxed">
                                            {t(section.contentKey)}
                                        </p>

                                        {/* Tips & Warnings */}
                                        <div className="space-y-4 mb-6">
                                            {section.tipKey && (
                                                <div className="flex gap-4 p-4 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-200">
                                                    <Lightbulb className="w-5 h-5 flex-shrink-0 text-blue-400" />
                                                    <p className="text-sm">{t(section.tipKey)}</p>
                                                </div>
                                            )}
                                            {section.warningKey && (
                                                <div className="flex gap-4 p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20 text-yellow-200">
                                                    <AlertTriangle className="w-5 h-5 flex-shrink-0 text-yellow-400" />
                                                    <p className="text-sm">{t(section.warningKey)}</p>
                                                </div>
                                            )}
                                        </div>

                                        {section.steps && (
                                            <div className="grid gap-4 bg-white/[0.02] p-6 rounded-2xl border border-white/5">
                                                {section.steps.map((step: any, sIdx: number) => (
                                                    <div key={sIdx} className="flex gap-4">
                                                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-xs font-bold border border-blue-500/20">
                                                            {sIdx + 1}
                                                        </div>
                                                        <div>
                                                            <h4 className="text-sm font-bold text-gray-200 mb-1">{t(step.titleKey)}</h4>
                                                            <p className="text-sm text-gray-500">{t(step.descriptionKey)}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Footer for the content card */}
                            <div className="mt-12 pt-8 border-t border-white/5 flex justify-end">
                                <span className="text-xs font-mono text-gray-600 uppercase tracking-widest">VANTAK Documentation v1.0</span>
                            </div>
                        </div>
                    ) : (
                        // Placeholder state when no module is selected (Desktop only)
                        <div className="text-center p-12 opacity-50">
                            <div className="w-24 h-24 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6">
                                <FileText className="w-10 h-10 text-gray-600" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-300 mb-2 uppercase">{t('manuals.placeholder.title')}</h3>
                            <p className="text-gray-500 max-w-sm mx-auto">
                                {t('manuals.placeholder.description')}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
